import React from 'react';
import { Menu as AntMenu, MenuProps as AntMenuProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface MenuProps extends AntMenuProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  menu: {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.md,
    lineHeight: theme.typography.lineHeight.normal,
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    borderColor: theme.color.semantic.border.subtle,
    color: theme.color.semantic.text.primary,
    borderRadius: theme.radius.sm,

    // Root menu styling
    '&.ant-menu': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderColor: theme.color.semantic.border.subtle,
      color: theme.color.semantic.text.primary,
    },

    // Horizontal mode
    '&.ant-menu-horizontal': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
      lineHeight: theme.typography.lineHeight.relaxed,

      '& > .ant-menu-item, & > .ant-menu-submenu': {
        borderBottom: `${theme.border.width.md} solid transparent`,
        marginTop: '-1px',
        marginBottom: 0,
        padding: `0 ${theme.spacing[4]}`,

        '&:hover': {
          color: theme.color.semantic.brand.default.secondary,
          borderBottomColor: theme.color.semantic.brand.default.secondary,
        },
      },

      '& > .ant-menu-item-selected, & > .ant-menu-submenu-selected': {
        color: theme.color.semantic.brand.default.primary,
        borderBottomColor: theme.color.semantic.brand.default.primary,
        backgroundColor: 'transparent',

        '&::after': {
          display: 'none',
        },
      },
    },

    // Vertical mode
    '&.ant-menu-vertical, &.ant-menu-vertical-left, &.ant-menu-vertical-right': {
      borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
    },

    // Inline mode
    '&.ant-menu-inline': {
      borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,

      '& .ant-menu-item, & .ant-menu-submenu-title': {
        width: '100%',
      },
    },

    // Menu item styling
    '& .ant-menu-item': {
      margin: 0,
      padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.md,
      fontWeight: theme.typography.fontWeight.regular,
      borderRadius: 0,
      transition: 'color 0.2s ease, background-color 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      lineHeight: theme.typography.lineHeight.normal,

      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
        backgroundColor: theme.color.semantic.background.neutral.subtle,
      },

      '&:active': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
      },
    },

    // Selected menu item
    '& .ant-menu-item-selected': {
      color: theme.color.semantic.brand.default.primary,
      backgroundColor: theme.color.semantic.brand.default.subtle,
      fontWeight: theme.typography.fontWeight.medium,

      '&::after': {
        display: 'none',
      },

      '&:hover': {
        color: theme.color.semantic.brand.default.secondary,
        backgroundColor: theme.color.semantic.brand.default.muted,
      },
    },

    // Active menu item
    '& .ant-menu-item-active': {
      color: theme.color.semantic.brand.default.primary,
    },

    // Disabled menu item
    '& .ant-menu-item-disabled': {
      color: `${theme.color.semantic.text.soft} !important`,
      backgroundColor: 'transparent !important',
      cursor: 'not-allowed',

      '& a': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed',
      },
    },

    // Submenu styling
    '& .ant-menu-submenu': {
      '& .ant-menu-submenu-title': {
        margin: 0,
        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.md,
        fontWeight: theme.typography.fontWeight.regular,
        borderRadius: 0,
        transition: 'color 0.2s ease, background-color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        lineHeight: theme.typography.lineHeight.normal,

        '&:hover': {
          color: theme.color.semantic.brand.default.primary,
          backgroundColor: theme.color.semantic.background.neutral.subtle,
        },

        '& .ant-menu-submenu-arrow': {
          color: theme.color.semantic.text.tertiary,
          transition: 'color 0.2s ease, transform 0.2s ease',

          '&::before, &::after': {
            backgroundColor: theme.color.semantic.background.neutral.strong,
          },
        },
      },
    },

    // Selected submenu
    '& .ant-menu-submenu-selected': {
      '& > .ant-menu-submenu-title': {
        color: theme.color.semantic.brand.default.primary,
        fontWeight: theme.typography.fontWeight.medium,
      },
    },

    // Open submenu
    '& .ant-menu-submenu-open': {
      '& > .ant-menu-submenu-title': {
        '& .ant-menu-submenu-arrow': {
          color: theme.color.semantic.brand.default.primary,

          '&::before, &::after': {
            backgroundColor: theme.color.semantic.brand.default.primary,
          },
        },
      },
    },

    // Disabled submenu
    '& .ant-menu-submenu-disabled': {
      '& .ant-menu-submenu-title': {
        color: `${theme.color.semantic.text.soft} !important`,
        backgroundColor: 'transparent !important',
        cursor: 'not-allowed',

        '& .ant-menu-submenu-arrow': {
          '&::before, &::after': {
            backgroundColor: `${theme.color.semantic.background.neutral.strong} !important`,
          },
        },
      },
    },

    // Nested submenu (sub-menu)
    '& .ant-menu-sub': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,

      '&.ant-menu-inline': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        border: 'none',

        '& .ant-menu-item': {
          paddingLeft: `${theme.spacing[8]} !important`,
        },
      },
    },

    // Menu item group
    '& .ant-menu-item-group': {
      '& .ant-menu-item-group-title': {
        padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
        color: theme.color.semantic.text.tertiary,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.semibold,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },

      '& .ant-menu-item-group-list': {
        '& .ant-menu-item': {
          paddingLeft: `${theme.spacing[6]} !important`,
        },
      },
    },

    // Divider
    '& .ant-menu-item-divider': {
      backgroundColor: theme.color.semantic.border.subtle,
      height: theme.border.width.sm,
      margin: `${theme.spacing[2]} 0`,
    },

    // Icons
    '& .ant-menu-item .anticon, & .ant-menu-submenu-title .anticon, & .ant-menu-item .ant-menu-item-icon, & .ant-menu-submenu-title .ant-menu-item-icon': {
      fontSize: theme.typography.fontSize.lg,
      marginRight: theme.spacing[3],
      verticalAlign: 'middle',
      color: 'inherit',
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: `${theme.spacing[4]} !important`,
      height: `${theme.spacing[4]} !important`,
      minWidth: theme.spacing[4],
      '& > svg, & > span > svg': {
        width: `${theme.spacing[4]} !important`,
        height: `${theme.spacing[4]} !important`,
        display: 'block',
      },
    },

    // Title text in menu items
    '& .ant-menu-title-content': {
      flex: 1,
      display: 'inline-block',
      verticalAlign: 'middle',
    },

    // Expand/collapse icons for inline mode
    '& .ant-menu-inline-collapsed': {
      '& > .ant-menu-item, & > .ant-menu-submenu > .ant-menu-submenu-title': {
        padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
        textAlign: 'center',
        justifyContent: 'center',

        '& .anticon': {
          margin: 0,
          fontSize: theme.typography.fontSize.lg,
          lineHeight: theme.typography.lineHeight.loose,
        },

        '& .ant-menu-item-icon': {
          margin: 0,
        },
      },
    },

    // Light theme override (default)
    '&.ant-menu-light': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderColor: theme.color.semantic.border.subtle,

      '& .ant-menu-item:hover, & .ant-menu-submenu-title:hover': {
        color: theme.color.semantic.brand.default.primary,
      },

      '& .ant-menu-item-selected': {
        color: theme.color.semantic.brand.default.primary,
        backgroundColor: theme.color.semantic.brand.default.subtle,
      },
    },

    // Override antd's dark theme with our semantic tokens
    '&.ant-menu-dark': {
      backgroundColor: theme.color.semantic.background.neutral.inverse,
      color: theme.color.semantic.text.inverse,

      '& .ant-menu-item': {
        color: theme.color.semantic.text.inverse,

        '&:hover': {
          color: theme.color.semantic.border.inverse,
          backgroundColor: theme.color.semantic.accent.inverse.soft,
        },
      },

      '& .ant-menu-item-selected': {
        color: theme.color.semantic.border.inverse,
        backgroundColor: theme.color.semantic.brand.default.primary,
      },

      '& .ant-menu-submenu-title': {
        color: theme.color.semantic.text.inverse,

        '&:hover': {
          color: theme.color.semantic.border.inverse,
          backgroundColor: theme.color.semantic.accent.inverse.soft,
        },

        '& .ant-menu-submenu-arrow': {
          '&::before, &::after': {
            backgroundColor: theme.color.semantic.text.soft,
          },
        },
      },

      '& .ant-menu-sub': {
        backgroundColor: theme.color.semantic.background.neutral.inverse,
      },

      '& .ant-menu-item-group-title': {
        color: theme.color.semantic.text.soft,
      },

      '& .ant-menu-item-divider': {
        backgroundColor: theme.color.semantic.accent.inverse.soft,
      },

      '& .ant-menu-item-disabled, & .ant-menu-submenu-disabled .ant-menu-submenu-title': {
        color: `${theme.color.semantic.text.tertiary} !important`,
      },
    },

    // Focus states for accessibility
    '& .ant-menu-item:focus-visible, & .ant-menu-submenu-title:focus-visible': {
      outline: `${theme.border.width.md} solid ${theme.color.semantic.brand.default.moderate}`,
      outlineOffset: '-2px',
    },

    // Danger item styling
    '& .ant-menu-item-danger': {
      color: theme.color.semantic.brand.danger.primary,

      '&:hover': {
        color: theme.color.semantic.brand.danger.muted,
        backgroundColor: theme.color.semantic.brand.danger.inverse,
      },

      '&.ant-menu-item-selected': {
        color: theme.color.semantic.brand.danger.primary,
        backgroundColor: theme.color.semantic.brand.danger.subtle,
      },
    },
  },

  // Popup menu styling (for dropdowns/submenus in vertical/horizontal mode)
  menuPopup: {
    '& .ant-menu': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
      borderRadius: theme.radius.sm,
      boxShadow: theme.shadow.md,

      '& .ant-menu-item': {
        margin: theme.spacing[1],
        padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
        borderRadius: theme.radius.xs,

        '&:first-child': {
          marginTop: theme.spacing[1],
        },

        '&:last-child': {
          marginBottom: theme.spacing[1],
        },
      },

      '& .ant-menu-submenu-title': {
        margin: theme.spacing[1],
        padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
        borderRadius: theme.radius.xs,
      },
    },
  },
}));

// Export SubMenu and MenuItem components from antd for convenience
export const SubMenu = AntMenu.SubMenu;
export const MenuItem = AntMenu.Item;
export const MenuItemGroup = AntMenu.ItemGroup;
export const MenuDivider = AntMenu.Divider;

export const Menu: React.FC<MenuProps> & {
  SubMenu: typeof AntMenu.SubMenu;
  Item: typeof AntMenu.Item;
  ItemGroup: typeof AntMenu.ItemGroup;
  Divider: typeof AntMenu.Divider;
} = ({
  className = '',
  mode = 'vertical',
  theme: menuTheme = 'light',
  ...rest
}) => {
  const classes = useStyles();

  const combinedClassName = `${classes.menu} ${className}`.trim();

  return (
    <AntMenu
      mode={mode}
      theme={menuTheme}
      {...rest}
      className={combinedClassName}
    />
  );
};

// Attach sub-components
Menu.SubMenu = AntMenu.SubMenu;
Menu.Item = AntMenu.Item;
Menu.ItemGroup = AntMenu.ItemGroup;
Menu.Divider = AntMenu.Divider;

export default Menu;
